import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { BasicButton } from '@payer-admin-ui/components-v2/src/buttons/BasicButton';
import { IconType } from '@payer-admin-ui/components-v2/src/icons/BasicIcon';
import { Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';
import { TextInput } from '../../Input/TextInput';
import { InfoTooltip } from '../../IndicatorIcon/InfoTooltip';
import { RoleCheck } from '../../RoleCheck/RoleCheck';
import { Role } from '../../../../components-v2/src/types/Role';
import { FetchingState } from '../../../../components-v2/src/types/state';
import styles from './InvoiceCustomerCard.module.scss';

export type RegNumberFieldProps = {
  countryCode: CountryCode;
  value: string;
  setValue: (value?: string) => void;
  deliveryType: DeliveryType;
  customerVatId?: string;
  disabled: boolean;
  getAddress: () => void;
  addressFetchState: FetchingState;
  resetGetAddressSuccess: () => void;
};

const RegNumberField = ({
  countryCode,
  value,
  setValue,
  deliveryType,
  customerVatId,
  disabled,
  getAddress,
  addressFetchState,
  resetGetAddressSuccess,
}: RegNumberFieldProps) => {
  const { t } = useTranslation();

  const regNumberIsNonOptional = deliveryType === DeliveryType.EINVOICE;
  const regNumberOrCustomerVatIdIsSpecified = value || customerVatId;

  useEffect(() => {
    if (addressFetchState?.success) {
      // TODO add snackbar message reflecting success
      resetGetAddressSuccess?.();
    }
  }, [addressFetchState?.success, resetGetAddressSuccess]);

  return (
    <Container className="p-0 m-0">
      <Row>
        <Col>
          <h5>{t('invoices.fields.registrationNumber')}</h5>
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInput
            value={value}
            setValue={regNumber => setValue(regNumber || undefined)}
            validators={[
              ...(regNumberIsNonOptional ? [Validators.notEmpty] : []),
              ...(regNumberOrCustomerVatIdIsSpecified ? [] : [Validators.notEmpty]),
              Validators.length(0, 48),
            ]}
            disabled={disabled}
            title=""
          />
        </Col>
      </Row>
      {!disabled && (
        <Row>
          <Col>
            <RoleCheck requiredRole={Role.CUSTOMER_READ}>
              <div className={styles.getAddressContainer}>
                <div className="mr-3">
                  <BasicButton
                    color="primary"
                    variant="contained"
                    loading={addressFetchState.isFetching}
                    startIcon={IconType.RELOAD}
                    onClick={getAddress}
                    disabled={addressFetchState.isFetching || !value || countryCode !== CountryCode.SE}
                  >
                    {t('components.cards.invoice.customer.getAddress')}
                  </BasicButton>
                </div>
                <div>
                  <InfoTooltip>{t('components.cards.invoice.customer.getAddressTooltip')}</InfoTooltip>
                </div>
              </div>
            </RoleCheck>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export { RegNumberField };
